import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  useMediaQuery,
  useTheme,
  Box,
  Stack,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Avatar,
  Menu,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import YouTubeIcon from "@mui/icons-material/YouTube";
import HelpIcon from '@mui/icons-material/Help';
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { logout } from "../../actions/userActions";
import DropdownButton from "../UI/DropdownButton";
import { useNavigate } from "react-router-dom";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import TermsDialog from "../../dialogs/TermsDialog";
import StarsIcon from "@mui/icons-material/Stars";
import PolicyIcon from "@mui/icons-material/Policy";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faStar } from "@fortawesome/free-solid-svg-icons";
import {
  hasSeenCreditsModal,
  requestCredits,
  resetCreditState,
  setCredits,
  updateCredits,
} from "../../actions/creditActions";
import PrivacyPolicyDialog from "../../dialogs/PrivacyPolicyDialog";
import CreditsInfoDialog from "../../dialogs/CreditsInfoDialog";
import CongratulationDialog from "../../dialogs/CongratulationDialog";
import InfoIcon from "@mui/icons-material/Info";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import CreditsHistoryDialog from "../../dialogs/CreditsHistoryDialog";
import FeedbackDialog from "../../dialogs/FeedbackDialog";
import { ToastContainer } from "react-toastify";
import ProfileModal from "../../dialogs/ProfileDialog";
import { 
  Menu as MenuFeather, 
  HelpCircle, 
  Info, 
  Bell, 
  User, 
  Settings, 
  LogOut, 
  FileText, 
  Shield 
} from 'react-feather';

const Header = ({ handleSidebarToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [openPrivacyPolicyDialog, setOpenPrivacyPolicyDialog] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [congratsModalOpen, setCongratsModalOpen] = useState(false);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const handleProfileClick = () => {
    setProfileOpen(true);
    handleClose();
  };
  const maxRefillCount = 3;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const { credits, creditRequestCount } = useSelector((state) => state.credit);

  const getFirstName = (name) => {
    return name ? name.split(" ")[0] : "";
  };

  // Function to find the AccountInfo based on homeAccountId
  const findAccountByHomeAccountId = (homeAccountId) => {
    return accounts.find((account) => account.homeAccountId === homeAccountId);
  };
  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleCongratsModalClose = () => {
    setCongratsModalOpen(false);
  };

  const handleRequestCredit = useCallback(async () => {
    if (creditRequestCount < maxRefillCount) {
      setLoading(true);
      try {
        const response = dispatch(requestCredits(userInfo._id));
        if (response?.success) {
          setCongratsModalOpen(true);
          handleClose();
          dispatch(
            updateCredits({
              credits: response.credits,
              expirationDate: response.expirationDate,
              creditRequestCount: creditRequestCount + 1,
            })
          );
          // fetchUserProfile();
        }
      } catch (error) {
        console.error("Failed to request credits:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [dispatch, userInfo._id, creditRequestCount]);

  const signOutClickHandler = (instance, accountToSignOut) => {
    instance.logout({
      account: accountToSignOut,
      postLogoutRedirectUri: "/",
    });
  };

  const logOut = () => {
    const homeAccountId = accounts[0]?.homeAccountId;
    const accountToSignOut = findAccountByHomeAccountId(homeAccountId);
    if (accountToSignOut) {
      signOutClickHandler(instance, accountToSignOut);
    } else {
      console.error("Account not found for the given homeAccountId");
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTermsClick = () => {
    setOpenTermsDialog(true);
    handleClose();
  };

  const handlePrivacyPolicyClick = () => {
    setOpenPrivacyPolicyDialog(true);
    handleClose();
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
    dispatch(resetCreditState());
    logOut();
  };

  const getTooltipTitle = () => {
    if (credits > 20) {
      return "You have enough credits. Please consume before requesting a refill.";
    } else if (creditRequestCount >= maxRefillCount) {
      return "You've reached the maximum number of refill requests. Please contact support for assistance.";
    } else {
      return `Refill Requests: ${creditRequestCount}/${maxRefillCount}`;
    }
  };

  const calculateProfileCompletion = (user) => {
    if (!user) return 0;

    const requiredFields = [
      "email",
      "phoneNumber",
      "userType",
      "designation",
      "barCouncil",
      "cnic",
    ];

    const optionalFields = ["organization", "officeAddress"];

    const totalWeight = requiredFields.length * 2 + optionalFields.length; // 100% total
    let completed = 0;

    // Check required fields (2% each)
    requiredFields.forEach((field) => {
      if (
        user[field] &&
        !["N/A", "Unknown", "Not provided"].includes(user[field])
      ) {
        completed += 2;
      }
    });

    // Check optional fields (1% each)
    optionalFields.forEach((field) => {
      if (
        user[field] &&
        !["N/A", "Unknown", "Not provided"].includes(user[field])
      ) {
        completed += 1;
      }
    });

    return Math.min(Math.round((completed / totalWeight) * 100), 100);
  };

  // Usage in component
  const profileCompletion = calculateProfileCompletion(userInfo);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, showNotifications]);

  return (
    <>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={50}
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          height: "64px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            pr: { xs: 2, sm: 3, lg: 4 }, // Right padding
            pl: "15px", // Left padding exactly 15px as tested
          }}
        >
          {/* Left side - Menu button, Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Hamburger Menu Button */}
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                p: 1,
                borderRadius: 1,
                color: "#6B7280",
                "&:hover": {
                  color: "#374151",
                  backgroundColor: "#F3F4F6",
                },
                "&:focus": {
                  outline: "none",
                  ring: 2,
                  ringColor: "#0073A6",
                },
              }}
            >
              <MenuFeather size={20} />
            </IconButton>
            
            {/* Logo */}
          <Box
            sx={{
                mx: 2,
                cursor: "pointer",
                display: "flex",
              alignItems: "center",
            }}
            onClick={() => navigate("/app/home")}
          >
            <img
                src="/images/legalSearchLogo2.png"
                alt="LawGPT Logo"
                style={{
                  height: "40px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          {/* Right side - Icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Feedback Button */}
            <Tooltip
              title="Feedback & Complaint"
              placement="bottom"
              arrow
            >
              <IconButton
                onClick={() => setOpenFeedbackDialog(true)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",
                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <HelpCircle size={20} />
              </IconButton>
            </Tooltip>

            {/* Credits Info */}
            <Tooltip title={getTooltipTitle()} placement="bottom" arrow>
              <IconButton
                onClick={handleModalOpen}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",
                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <Info size={20} />
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Box className="notifications-container" sx={{ position: "relative" }}>
            <IconButton
                onClick={() => setShowNotifications(!showNotifications)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",
                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <Bell size={20} />
                  {/* Notification badge can be added here if needed */}
                </Box>
              </IconButton>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    mt: 1,
                    width: 320,
                    borderRadius: 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    zIndex: 1000,
                    py: 1,
                  }}
                >
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#111827" }}>
                      Notifications
                    </Typography>
                  </Box>
                  <Box sx={{ maxHeight: 256, overflowY: "auto" }}>
                    <Box sx={{ px: 3, py: 2, textAlign: "center", color: "#6B7280" }}>
                      No new notifications
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* User Menu */}
            <Box className="user-menu-container" sx={{ position: "relative" }}>
              <IconButton
                onClick={() => setShowUserMenu(!showUserMenu)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",
                "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <User size={24} />
              </IconButton>
              
              {/* User Dropdown Menu */}
              {showUserMenu && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    mt: 1,
                    width: 240,
                    borderRadius: 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    zIndex: 1000,
                    py: 1,
                  }}
                >
                  {/* User Info */}
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#111827" }}>
                      {userInfo?.name || "User"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {userInfo?.email || "user@example.com"}
                    </Typography>
                  </Box>

                  {/* Menu Items */}
                  <MenuItem
                    onClick={handleProfileClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <User size={18} style={{ marginRight: 8, color: "#6B7280" }} />
                    <Typography variant="body2">Profile</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={handleTermsClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <FileText size={18} style={{ marginRight: 8, color: "#6B7280" }} />
                    <Typography variant="body2">Terms of Service</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={handlePrivacyPolicyClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <Shield size={18} style={{ marginRight: 8, color: "#6B7280" }} />
                    <Typography variant="body2">Privacy Policy</Typography>
                  </MenuItem>

                  <Divider sx={{ my: 1 }} />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#DC2626",
                      "&:hover": {
                        backgroundColor: "#FEF2F2",
                      },
                    }}
                  >
                    <LogOut size={18} style={{ marginRight: 8, color: "#DC2626" }} />
                    <Typography variant="body2">Logout</Typography>
                  </MenuItem>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <CongratulationDialog
        // open={congratsModalOpen} // Originally true
        open={false} // Temporarily disabled
        onClose={handleCongratsModalClose}
        credits={100}
      />
      <CreditsInfoDialog
        // open={isModalOpen} // Originally true
        open={false} // Temporarily disabled
        onClose={handleModalClose}
      />
      <FeedbackDialog
        open={openFeedbackDialog}
        onClose={() => setOpenFeedbackDialog(false)}
      />
      {profileOpen && (
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
        />
      )}
      <TermsDialog
        open={openTermsDialog}
        onClose={() => setOpenTermsDialog(false)}
      />
      <PrivacyPolicyDialog
        open={openPrivacyPolicyDialog}
        onClose={() => setOpenPrivacyPolicyDialog(false)}
      />
      <ToastContainer />
    </>
  );
};

export default Header;
